import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../utils/seo';
import { useNavigate } from 'react-router-dom';
import MobileStickyCTA from '@/components/feature/MobileStickyCTA';

export default function WhatIsAddiction() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-slide functionality
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://thornhilltreatment.com';

  useSEO({
    title: 'What is Addiction? | Substance Use Disorder Guide',
    description: 'Learn about addiction, its causes, symptoms, and treatment options. Expert addiction education from Thornhill Drug-Mart serving Richmond Hill, Vaughan, and North York. Professional methadone and Suboxone treatment programs available.',
    keywords: 'what is addiction, substance use disorder, addiction treatment, opioid addiction, alcohol addiction, addiction help Thornhill, addiction education, addiction recovery',
    canonical: `${siteUrl}/what-is-addiction`,
    ogType: 'article',
    schema: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "What is Addiction? Understanding Substance Use Disorder",
      "description": "Comprehensive guide to understanding addiction, its causes, symptoms, and treatment options available in Thornhill, Ontario",
      "url": `${siteUrl}/what-is-addiction`,
      "publisher": {
        "@type": "Organization",
        "name": "Thornhill Drug-Mart",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "8108 Yonge St Unit 3-4",
          "addressLocality": "Thornhill",
          "addressRegion": "ON",
          "postalCode": "L4J 1W4",
          "addressCountry": "CA"
        }
      }
    }
  });

  const slides = [
    {
      image: 'https://readdy.ai/api/search-image?query=compassionate%20healthcare%20professional%20counseling%20patient%20in%20modern%20bright%20medical%20office%2C%20supportive%20addiction%20treatment%20consultation%2C%20warm%20teal%20and%20white%20colors%2C%20hopeful%20recovery%20atmosphere%2C%20professional%20medical%20setting%2C%20understanding%20and%20empathy%2C%20clean%20contemporary%20design&width=1920&height=800&seq=addiction-education-slide-1&orientation=landscape',
      title: 'Understanding Addiction',
      description: 'Learn about addiction as a treatable medical condition, not a moral failing'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=person%20on%20recovery%20journey%20walking%20toward%20bright%20future%2C%20hope%20and%20healing%20symbolism%2C%20sunrise%20over%20peaceful%20landscape%2C%20new%20beginning%20atmosphere%2C%20soft%20teal%20and%20warm%20colors%2C%20inspirational%20healthcare%20imagery%2C%20path%20to%20wellness%2C%20serene%20medical%20environment&width=1920&height=800&seq=addiction-education-slide-2&orientation=landscape',
      title: 'Recovery is Possible',
      description: 'Evidence-based treatment programs that support lasting recovery and wellness'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=supportive%20medical%20team%20in%20modern%20addiction%20treatment%20clinic%2C%20professional%20healthcare%20environment%2C%20clean%20bright%20facility%20with%20teal%20accents%2C%20medication-assisted%20treatment%20setting%2C%20recovery-focused%20atmosphere%2C%20welcoming%20healthcare%20space%2C%20expert%20medical%20staff&width=1920&height=800&seq=addiction-education-slide-3&orientation=landscape',
      title: 'Expert Treatment Available',
      description: 'Professional addiction treatment services in Thornhill serving the Greater Toronto Area'
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="min-h-screen bg-background-100">
      <Navbar isScrolled={isScrolled} />
      
      {/* Hero Slider Section */}
      <section className="relative min-h-[70vh] sm:min-h-screen flex items-center justify-center overflow-hidden">
        {/* Slides */}
        <div className="absolute inset-0">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
            </div>
          ))}
        </div>

        {/* Content Overlay */}
        <div className="relative z-10 w-full px-4 sm:px-6 py-16 sm:py-24">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center justify-center mb-6 sm:mb-8" aria-label="Breadcrumb">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center text-white/70 hover:text-white transition-colors cursor-pointer"
                  aria-label="Go to homepage"
                >
                  <i className="ri-home-4-line text-base sm:text-lg"></i>
                  <span className="ml-2 text-xs sm:text-sm font-medium">Home</span>
                </button>
                <i className="ri-arrow-right-s-line text-white/50 text-base sm:text-lg"></i>
                <button
                  onClick={() => navigate('/addiction-services')}
                  className="text-white/70 hover:text-white transition-colors cursor-pointer text-xs sm:text-sm font-medium"
                  aria-label="Go to addiction services"
                >
                  Addiction Services
                </button>
                <i className="ri-arrow-right-s-line text-white/50 text-base sm:text-lg"></i>
                <span className="text-primary-400 text-xs sm:text-sm font-semibold">What is Addiction</span>
              </div>
            </nav>

            {/* Badge - Centered and Smaller */}
            <div className="flex justify-center mb-4 sm:mb-6">
              <div className="inline-flex items-center space-x-2 bg-primary-50 px-3 py-1.5 rounded-full">
                <i className="ri-book-open-line text-primary-700 text-sm"></i>
                <span className="text-primary-700 text-xs font-semibold uppercase tracking-wide">Education & Resources</span>
              </div>
            </div>

            {/* Slide Content */}
            <header className="text-center mb-8 sm:mb-10" key={currentSlide}>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                {slides[currentSlide].title}
              </h1>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed">
                {slides[currentSlide].description}
              </p>

              {/* Action Buttons - 2 Column Grid */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 max-w-2xl mx-auto">
                <a
                  href="/addiction-services"
                  className="bg-primary-600 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-primary-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
                  aria-label="View addiction treatment services"
                >
                  <i className="ri-heart-pulse-line mr-1 sm:mr-2"></i>
                  <span className="hidden sm:inline">Treatment Services</span>
                  <span className="sm:hidden">Treatments</span>
                </a>
                <a
                  href="tel:9057099985"
                  className="bg-background-50 text-foreground-950 px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-background-200/70 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
                  aria-label="Call Thornhill Drug-Mart at 905-709-9985"
                >
                  <i className="ri-phone-line mr-1 sm:mr-2"></i>
                  <span className="hidden sm:inline">(905) 709-9985</span>
                  <span className="sm:hidden">Call Now</span>
                </a>
              </div>
            </header>
          </div>
        </div>

        {/* Navigation Arrows - Desktop */}
        <button
          onClick={prevSlide}
          className="hidden lg:flex absolute left-4 xl:left-8 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-md hover:bg-white/30 rounded-full items-center justify-center transition-all cursor-pointer group z-20"
          aria-label="Previous slide"
        >
          <i className="ri-arrow-left-s-line text-white text-2xl group-hover:scale-110 transition-transform"></i>
        </button>
        <button
          onClick={nextSlide}
          className="hidden lg:flex absolute right-4 xl:right-8 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-md hover:bg-white/30 rounded-full items-center justify-center transition-all cursor-pointer group z-20"
          aria-label="Next slide"
        >
          <i className="ri-arrow-right-s-line text-white text-2xl group-hover:scale-110 transition-transform"></i>
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 flex items-center space-x-2 sm:space-x-3 z-20">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`transition-all cursor-pointer ${
                index === currentSlide
                  ? 'w-8 sm:w-10 h-2 bg-white'
                  : 'w-2 h-2 bg-white/50 hover:bg-white/70'
              } rounded-full`}
              aria-label={`Go to slide ${index + 1}`}
            ></button>
          ))}
        </div>

        {/* Scroll Down Arrow */}
        <div className="absolute bottom-16 sm:bottom-20 left-1/2 -translate-x-1/2 z-20 animate-bounce">
          <i className="ri-arrow-down-line text-white text-2xl sm:text-3xl"></i>
        </div>
      </section>

      {/* Main Content */}
      <article className="py-16 lg:py-24">
        <div className="container mx-auto px-4 max-w-4xl">
          
          {/* Understanding Addiction Section */}
          <section className="mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground-950 mb-6">Understanding Addiction</h2>
            <p className="text-lg text-foreground-700 mb-4 leading-relaxed">
              One of the most significant risks of substance use is that it can lead to <strong className="text-primary-700">addiction</strong>.
            </p>
            <p className="text-lg text-foreground-700 mb-6 leading-relaxed">
              Despite affecting millions of people each year, this disease is often misunderstood. Let's look at what addiction is and how it can be treated at our <strong className="text-primary-700">addiction pharmacy</strong> and <strong className="text-primary-700">addiction clinic</strong> serving <strong className="text-primary-700">Thornhill</strong>, <strong className="text-primary-700">Richmond Hill</strong>, <strong className="text-primary-700">Vaughan</strong>, and <strong className="text-primary-700">North York</strong>.
            </p>

            <h3 className="text-2xl lg:text-3xl font-bold text-foreground-950 mb-4 mt-12">What is Addiction?</h3>
            <p className="text-lg text-foreground-700 mb-4 leading-relaxed">
              Addiction, clinically known as a severe <strong>Substance Use Disorder (SUD)</strong>, is a chronic brain disease that causes a person to compulsively seek out and use a substance, regardless of the harm it causes. It is not a moral failing or a lack of willpower.
            </p>
            <p className="text-lg text-foreground-700 mb-6 leading-relaxed">
              According to the DSM-5, a diagnosis is made based on a person meeting at least two of 11 criteria over a 12-month period. These criteria fall into four main categories:
            </p>

            <div className="bg-primary-50 rounded-xl p-8 mb-8">
              <ul className="space-y-6">
                <li className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-foreground-950 mb-2">Impaired Control</h4>
                    <p className="text-foreground-700">Using more of a substance or for longer than intended; wanting to cut down or stop but not being able to; spending a lot of time getting, using, or recovering from the substance; experiencing intense cravings.</p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-foreground-950 mb-2">Social Impairment</h4>
                    <p className="text-foreground-700">Continuing to use even when it causes problems in relationships; giving up important social, occupational, or recreational activities because of substance use.</p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-foreground-950 mb-2">Risky Use</h4>
                    <p className="text-foreground-700">Using substances in situations where it is physically dangerous; continuing use despite knowing it's causing or worsening physical or psychological problems.</p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-foreground-950 mb-2">Pharmacological Criteria</h4>
                    <p className="text-foreground-700">Needing more of the substance to get the desired effect (tolerance); developing withdrawal symptoms when use is stopped.</p>
                  </div>
                </li>
              </ul>
            </div>

            <p className="text-lg text-foreground-700 mb-6 leading-relaxed">
              The severity of the SUD is determined by how many criteria a person meets. Addiction is a life-threatening condition, but with the right support from our <strong className="text-primary-700">addiction clinic</strong> and <strong className="text-primary-700">pharmacy</strong>, recovery is possible.
            </p>
          </section>

          {/* How Addiction Happens */}
          <section className="mb-16">
            <h3 className="text-2xl lg:text-3xl font-bold text-foreground-950 mb-6">How Addiction Happens in the Brain</h3>
            <p className="text-lg text-foreground-700 mb-6 leading-relaxed">
              Addiction develops through a cycle often driven by one of two motivations:
            </p>

            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-8">
              <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-6 sm:p-8 border border-primary-200">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-primary-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-arrow-up-circle-line text-white text-2xl sm:text-3xl"></i>
                </div>
                <h4 className="text-lg sm:text-xl font-bold text-foreground-950 mb-3">Positive Reinforcement</h4>
                <p className="text-sm sm:text-base text-foreground-700 leading-relaxed">A person takes a drug because it produces a powerful feeling of pleasure or euphoria (a "high"). The brain remembers this reward and creates a strong desire to repeat the experience.</p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-6 sm:p-8 border border-primary-200">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-primary-600 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-arrow-down-circle-line text-white text-2xl sm:text-3xl"></i>
                </div>
                <h4 className="text-lg sm:text-xl font-bold text-foreground-950 mb-3">Negative Reinforcement</h4>
                <p className="text-sm sm:text-base text-foreground-700 leading-relaxed">A person takes a drug to relieve negative feelings like stress, anxiety, or depression. The substance provides temporary relief, reinforcing its use as a coping mechanism.</p>
              </div>
            </div>

            <p className="text-lg text-foreground-700 mb-6 leading-relaxed">
              In both scenarios, the brain's reward system is flooded with dopamine. Over time, the brain adapts by reducing its natural dopamine production, leading to tolerance (needing more of the drug to feel good) and dependence (needing the drug just to feel normal and avoid unpleasant withdrawal symptoms). To learn more about <a href="/drug-use" className="text-primary-700 hover:text-primary-800 underline underline-offset-2 transition-colors font-semibold">how drug use leads to dependency</a>, visit our detailed guide.
            </p>
          </section>

          {/* Addiction vs Dependence */}
          <section className="mb-16 bg-background-100 rounded-2xl p-6 sm:p-8 lg:p-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground-950 mb-6 sm:mb-8">Addiction vs Dependence</h2>
            <p className="text-base sm:text-lg text-foreground-700 mb-6 sm:mb-8 leading-relaxed">
              Addiction and dependence are different, but these issues often go hand-in-hand. It's possible to be dependent on a substance without being addicted.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
              <div className="bg-background-50 rounded-xl p-6 sm:p-8 shadow-sm">
                <h3 className="text-xl sm:text-2xl font-bold text-primary-700 mb-4">Addiction (SUD)</h3>
                <ul className="space-y-3 sm:space-y-4">
                  <li className="flex gap-3">
                    <i className="ri-checkbox-circle-fill text-primary-600 text-lg sm:text-xl flex-shrink-0 mt-1"></i>
                    <span className="text-sm sm:text-base text-foreground-700">A brain disease involving compulsive drug-seeking and use, despite harmful consequences</span>
                  </li>
                  <li className="flex gap-3">
                    <i className="ri-checkbox-circle-fill text-primary-600 text-lg sm:text-xl flex-shrink-0 mt-1"></i>
                    <span className="text-sm sm:text-base text-foreground-700">Characterized by an inability to control use and negative impacts on work, school, and relationships</span>
                  </li>
                  <li className="flex gap-3">
                    <i className="ri-checkbox-circle-fill text-primary-600 text-lg sm:text-xl flex-shrink-0 mt-1"></i>
                    <span className="text-sm sm:text-base text-foreground-700">Relates to the psychological and behavioral aspects of the user's relationship with the substance</span>
                  </li>
                </ul>
              </div>

              <div className="bg-background-50 rounded-xl p-6 sm:p-8 shadow-sm">
                <h3 className="text-xl sm:text-2xl font-bold text-primary-700 mb-4">Physical Dependence</h3>
                <ul className="space-y-3 sm:space-y-4">
                  <li className="flex gap-3">
                    <i className="ri-checkbox-circle-fill text-primary-600 text-lg sm:text-xl flex-shrink-0 mt-1"></i>
                    <span className="text-sm sm:text-base text-foreground-700">The body adapts to the presence of a drug, leading to tolerance (requiring higher doses for the same effect)</span>
                  </li>
                  <li className="flex gap-3">
                    <i className="ri-checkbox-circle-fill text-primary-600 text-lg sm:text-xl flex-shrink-0 mt-1"></i>
                    <span className="text-sm sm:text-base text-foreground-700">When the drug is stopped, the person experiences physical and psychological withdrawal symptoms</span>
                  </li>
                  <li className="flex gap-3">
                    <i className="ri-checkbox-circle-fill text-primary-600 text-lg sm:text-xl flex-shrink-0 mt-1"></i>
                    <span className="text-sm sm:text-base text-foreground-700">Dependence can occur with many prescription medications (like opioids or antidepressants) even when taken as directed</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-6 sm:mt-8 bg-primary-100 border-l-4 border-primary-600 p-4 sm:p-6 rounded-r-lg">
              <p className="text-sm sm:text-base lg:text-lg text-foreground-800 italic leading-relaxed">
                Physical dependence often contributes to the cycle of addiction, as a person may continue using a drug simply to avoid the pain of withdrawal.
              </p>
            </div>
          </section>

          {/* Statistics Section */}
          <section className="mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground-950 mb-6 sm:mb-8">Drug Addiction Statistics</h2>
            <p className="text-base sm:text-lg text-foreground-700 mb-6 sm:mb-8 leading-relaxed">
              The latest national data reveals the widespread impact of substance use and addiction:
            </p>

            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-8">
              <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl p-6 sm:p-8 text-white">
                <div className="text-4xl sm:text-5xl font-bold mb-2">48.7M</div>
                <p className="text-sm sm:text-base lg:text-lg text-primary-100 leading-relaxed">People aged 12+ had a Substance Use Disorder in the past year</p>
              </div>
              <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl p-6 sm:p-8 text-white">
                <div className="text-4xl sm:text-5xl font-bold mb-2">75.8M</div>
                <p className="text-sm sm:text-base lg:text-lg text-primary-100 leading-relaxed">People used illicit drugs in the past year</p>
              </div>
              <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl p-6 sm:p-8 text-white">
                <div className="text-4xl sm:text-5xl font-bold mb-2">107K+</div>
                <p className="text-sm sm:text-base lg:text-lg text-primary-100 leading-relaxed">Drug-involved overdose deaths in 2023</p>
              </div>
              <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl p-6 sm:p-8 text-white">
                <div className="text-4xl sm:text-5xl font-bold mb-2">Majority</div>
                <p className="text-sm sm:text-base lg:text-lg text-primary-100 leading-relaxed">With SUD do not receive any treatment</p>
              </div>
            </div>
          </section>

          {/* Who Does Addiction Affect */}
          <section className="mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground-950 mb-6">Who Does Addiction Affect?</h2>
            <p className="text-base sm:text-lg text-foreground-700 mb-6 leading-relaxed">
              <strong>Addiction can happen to anyone</strong>, regardless of age, gender, race, or socioeconomic status. Our <strong className="text-primary-700">addiction pharmacy</strong> in <strong className="text-primary-700">Thornhill</strong> serves diverse communities across <strong className="text-primary-700">Richmond Hill</strong>, <strong className="text-primary-700">Vaughan</strong>, and <strong className="text-primary-700">North York</strong>. If you are unsure whether you or a loved one may have a problem, our <a href="/are-you-addicted" className="text-primary-700 hover:text-primary-800 underline underline-offset-2 transition-colors font-semibold">addiction self-assessment guide</a> can help you identify warning signs.
            </p>
            <p className="text-base sm:text-lg text-foreground-700 mb-6 leading-relaxed">
              Research indicates that a combination of factors influences a person's risk:
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex gap-3 sm:gap-4 items-start bg-background-50 border border-background-200 rounded-lg p-4 sm:p-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-dna-line text-primary-600 text-lg sm:text-xl"></i>
                </div>
                <div>
                  <h4 className="text-lg sm:text-xl font-bold text-foreground-950 mb-2">Genetics</h4>
                  <p className="text-sm sm:text-base text-foreground-700 leading-relaxed">A family history of addiction can account for about half of a person's risk.</p>
                </div>
              </div>
              <div className="flex gap-3 sm:gap-4 items-start bg-background-50 border border-background-200 rounded-lg p-4 sm:p-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-earth-line text-primary-600 text-lg sm:text-xl"></i>
                </div>
                <div>
                  <h4 className="text-lg sm:text-xl font-bold text-foreground-950 mb-2">Environment</h4>
                  <p className="text-sm sm:text-base text-foreground-700 leading-relaxed">Factors like peer pressure, stress, trauma, and lack of parental supervision can increase risk, especially for adolescents.</p>
                </div>
              </div>
              <div className="flex gap-3 sm:gap-4 items-start bg-background-50 border border-background-200 rounded-lg p-4 sm:p-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-brain-line text-primary-600 text-lg sm:text-xl"></i>
                </div>
                <div>
                  <h4 className="text-lg sm:text-xl font-bold text-foreground-950 mb-2">Brain Development</h4>
                  <p className="text-sm sm:text-base text-foreground-700 leading-relaxed">The prefrontal cortex, which governs decision-making and impulse control, is not fully developed until the mid-20s, making teenagers particularly vulnerable to risky substance use.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Types of Drug Addiction */}
          <section className="mb-16 bg-primary-50 rounded-2xl p-6 sm:p-8 lg:p-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground-950 mb-6">Different Types of Drug Addiction</h2>
            <p className="text-base sm:text-lg text-foreground-700 mb-6 sm:mb-8 leading-relaxed">
              A person can develop an addiction to both illicit and prescription drugs. Our <strong className="text-primary-700">methadone pharmacy</strong> and <strong className="text-primary-700">Suboxone clinic</strong> provides specialized treatment for various substance use disorders.
            </p>

            <div className="mb-8">
              <h3 className="text-xl sm:text-2xl font-bold text-foreground-950 mb-4">Most Commonly Misused Prescription Drugs:</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-background-50 rounded-lg p-5 sm:p-6 shadow-sm">
                  <div className="w-10 h-10 flex items-center justify-center mb-3">
                    <i className="ri-capsule-line text-primary-600 text-3xl sm:text-4xl"></i>
                  </div>
                  <h4 className="text-base sm:text-lg font-bold text-foreground-950 mb-2">Opioids</h4>
                  <p className="text-sm text-foreground-700 leading-relaxed">Painkillers including prescription medications</p>
                </div>
                <div className="bg-background-50 rounded-lg p-5 sm:p-6 shadow-sm">
                  <div className="w-10 h-10 flex items-center justify-center mb-3">
                    <i className="ri-medicine-bottle-line text-primary-600 text-3xl sm:text-4xl"></i>
                  </div>
                  <h4 className="text-base sm:text-lg font-bold text-foreground-950 mb-2">Benzodiazepines</h4>
                  <p className="text-sm text-foreground-700 leading-relaxed">Sedatives and anti-anxiety medications</p>
                </div>
                <div className="bg-background-50 rounded-lg p-5 sm:p-6 shadow-sm">
                  <div className="w-10 h-10 flex items-center justify-center mb-3">
                    <i className="ri-pulse-line text-primary-600 text-3xl sm:text-4xl"></i>
                  </div>
                  <h4 className="text-base sm:text-lg font-bold text-foreground-950 mb-2">Amphetamines</h4>
                  <p className="text-sm text-foreground-700 leading-relaxed">ADHD medications and stimulants</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-foreground-950 mb-4">Most Common Illicit Drugs:</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-background-50 rounded-lg p-5 sm:p-6 shadow-sm">
                  <div className="w-10 h-10 flex items-center justify-center mb-3">
                    <i className="ri-leaf-line text-primary-600 text-3xl sm:text-4xl"></i>
                  </div>
                  <h4 className="text-base sm:text-lg font-bold text-foreground-950 mb-2">Cannabis</h4>
                  <p className="text-sm text-foreground-700 leading-relaxed">Though now legal in some regions</p>
                </div>
                <div className="bg-background-50 rounded-lg p-5 sm:p-6 shadow-sm">
                  <div className="w-10 h-10 flex items-center justify-center mb-3">
                    <i className="ri-syringe-line text-primary-600 text-3xl sm:text-4xl"></i>
                  </div>
                  <h4 className="text-base sm:text-lg font-bold text-foreground-950 mb-2">Heroin</h4>
                  <p className="text-sm text-foreground-700 leading-relaxed">Highly addictive opioid</p>
                </div>
                <div className="bg-background-50 rounded-lg p-5 sm:p-6 shadow-sm">
                  <div className="w-10 h-10 flex items-center justify-center mb-3">
                    <i className="ri-flask-line text-primary-600 text-3xl sm:text-4xl"></i>
                  </div>
                  <h4 className="text-base sm:text-lg font-bold text-foreground-950 mb-2">Methamphetamine</h4>
                  <p className="text-sm text-foreground-700 leading-relaxed">Crystal meth and similar stimulants</p>
                </div>
              </div>
            </div>
          </section>

          {/* Treatment Section */}
          <section className="mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground-950 mb-6">Drug Addiction Treatment &amp; Support</h2>
            <p className="text-base sm:text-lg text-foreground-700 mb-6 leading-relaxed">
              No matter the substance or the severity of the addiction, know that <strong>there is always hope</strong>.
            </p>
            <p className="text-base sm:text-lg text-foreground-700 mb-6 sm:mb-8 leading-relaxed">
              Addiction is a treatable disease, and recovery is possible with the help of health professionals and a strong support system at our <strong className="text-primary-700">addiction clinic</strong> serving <strong className="text-primary-700">Thornhill</strong>, <strong className="text-primary-700">Richmond Hill</strong>, <strong className="text-primary-700">Vaughan</strong>, and <strong className="text-primary-700">North York</strong>.
            </p>

            <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl p-6 sm:p-8 lg:p-12 text-white mb-12">
              <h3 className="text-2xl sm:text-3xl font-bold mb-6">Treatment Options Available</h3>
              
              <div className="space-y-4 sm:space-y-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 sm:p-6">
                  <h4 className="text-xl sm:text-2xl font-bold mb-3 flex items-center gap-2 sm:gap-3">
                    <i className="ri-heart-pulse-line text-2xl sm:text-3xl"></i>
                    Detoxification (Detox)
                  </h4>
                  <p className="text-sm sm:text-base text-primary-50 leading-relaxed">The recovery process usually begins with a detox program. Medical detoxification helps you safely clear the drug from your system while managing withdrawal symptoms under supervision.</p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 sm:p-6">
                  <h4 className="text-xl sm:text-2xl font-bold mb-3 flex items-center gap-2 sm:gap-3">
                    <i className="ri-hospital-line text-2xl sm:text-3xl"></i>
                    Rehabilitation (Rehab)
                  </h4>
                  <p className="text-sm sm:text-base text-primary-50 leading-relaxed">Following detox, you will receive care at a treatment center to help you in the early days of sobriety. Typical drug addiction treatment combines medical monitoring with behavioral therapies.</p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 sm:p-6">
                  <h4 className="text-xl sm:text-2xl font-bold mb-3 flex items-center gap-2 sm:gap-3">
                    <i className="ri-user-heart-line text-2xl sm:text-3xl"></i>
                    Medication-Assisted Treatment
                  </h4>
                  <p className="text-sm sm:text-base text-primary-50 leading-relaxed">Our <a href="/methadone-treatment" className="text-white hover:text-primary-100 underline underline-offset-2 transition-colors font-semibold">methadone pharmacy</a> and <a href="/suboxone-treatment" className="text-white hover:text-primary-100 underline underline-offset-2 transition-colors font-semibold">Suboxone clinic</a> provides evidence-based medication-assisted treatment (MAT) including Methadone, Suboxone, and Kadian programs.</p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 sm:p-6">
                  <h4 className="text-xl sm:text-2xl font-bold mb-3 flex items-center gap-2 sm:gap-3">
                    <i className="ri-group-line text-2xl sm:text-3xl"></i>
                    Post-Treatment Support (Aftercare)
                  </h4>
                  <p className="text-sm sm:text-base text-primary-50 leading-relaxed">After formal treatment, most people in recovery continue to receive support through aftercare programs that help them maintain sobriety and prevent relapse. We also provide <a href="/naloxone-kits" className="text-white hover:text-primary-100 underline underline-offset-2 transition-colors font-semibold">free naloxone kits</a> for overdose prevention — an essential safety tool for anyone in recovery or supporting someone who is.</p>
                </div>
              </div>
            </div>

            <div className="bg-background-50 rounded-2xl p-6 sm:p-8 lg:p-12">
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground-950 mb-6">For Friends and Family Members</h3>
              <p className="text-base sm:text-lg text-foreground-700 mb-6 leading-relaxed">
                Addiction affects more than just the person using substances. Friends and family members often deal with significant stress and emotional pain. Many resources are available to help you cope with this process and better understand addiction.
              </p>
              <p className="text-base sm:text-lg text-foreground-700 leading-relaxed">
                Support groups like Al-Anon or Nar-Anon provide guidance and healing for the loved ones of people with addiction.
              </p>
            </div>
          </section>

          {/* Relapse Section */}
          <section className="mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground-950 mb-6">What About Addiction Relapse?</h2>
            <p className="text-base sm:text-lg text-foreground-700 mb-6 leading-relaxed">
              It is important to remember that relapse is a common part of the recovery process and does not mean that treatment has failed. Addiction is a chronic, relapsing disease, much like asthma or diabetes. A return to use is a sign that the treatment plan needs to be adjusted or that new strategies are needed.
            </p>
            
            <div className="bg-primary-100 border-l-4 border-primary-600 p-5 sm:p-6 lg:p-8 rounded-r-xl mb-8">
              <p className="text-sm sm:text-base lg:text-lg text-foreground-800 italic mb-4 leading-relaxed">
                "Relapse rates for people treated for substance use disorders are compared with those for people treated for high blood pressure and asthma. Relapse is common and similar across these illnesses. Therefore, substance use disorders should be treated like any other chronic illness. Relapse serves as a sign for resumed, modified, or new treatment."
              </p>
              <p className="text-sm sm:text-base text-foreground-700 font-semibold">— National Institute on Drug Abuse</p>
            </div>
          </section>

          {/* Recovery Journey */}
          <section className="mb-16 bg-gradient-to-br from-primary-50 to-white rounded-2xl p-6 sm:p-8 lg:p-12 border border-primary-200">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground-950 mb-6">A Lifelong Journey of Recovery</h2>
            <p className="text-base sm:text-lg text-foreground-700 mb-6 leading-relaxed">
              Understanding addiction as a treatable brain disease is the first step toward breaking down the stigma that prevents many from seeking help. While the path can be challenging, millions of people live in long-term recovery.
            </p>
            <p className="text-base sm:text-lg text-foreground-700 mb-6 sm:mb-8 leading-relaxed">
              With evidence-based treatment at our <strong className="text-primary-700">addiction pharmacy</strong> and <strong className="text-primary-700">clinic</strong>, a strong support system, and a commitment to new, healthy habits, you or your loved one can reclaim your life from addiction.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="/addiction-services" 
                className="inline-flex items-center justify-center gap-2 bg-primary-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg hover:bg-primary-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg"
                aria-label="View addiction treatment programs"
              >
                <span className="hidden sm:inline">View Our Treatment Programs</span>
                <span className="sm:hidden">Treatment Programs</span>
                <i className="ri-arrow-right-line text-lg sm:text-xl"></i>
              </a>
              <a 
                href="/location" 
                className="inline-flex items-center justify-center gap-2 bg-white text-primary-600 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg border-2 border-primary-600 hover:bg-primary-50 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg"
                aria-label="Find our location in Thornhill"
              >
                <i className="ri-map-pin-line text-lg sm:text-xl"></i>
                <span className="hidden sm:inline">Find Our Location</span>
                <span className="sm:hidden">Our Location</span>
              </a>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground-950 mb-8">Frequently Asked Questions</h2>
            
            <div className="space-y-4">
              <details className="bg-background-50 border border-background-200 rounded-lg overflow-hidden group">
                <summary className="cursor-pointer p-6 font-semibold text-lg text-foreground-950 hover:bg-background-100 transition-colors flex justify-between items-center">
                  What is Addiction?
                  <i className="ri-arrow-down-s-line text-2xl text-purple-600 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-6 pb-6 text-foreground-700 leading-relaxed">
                  Addiction is a chronic, treatable brain disease characterized by compulsive drug seeking and use, despite harmful consequences. It fundamentally changes the brain's structure and function, particularly in areas related to reward, stress, and self-control. Clinically, a severe addiction is diagnosed as a Substance Use Disorder (SUD). It is not a lack of willpower.
                </div>
              </details>

              <details className="bg-background-50 border border-background-200 rounded-lg overflow-hidden group">
                <summary className="cursor-pointer p-6 font-semibold text-lg text-foreground-950 hover:bg-background-100 transition-colors flex justify-between items-center">
                  What are the signs of addiction?
                  <i className="ri-arrow-down-s-line text-2xl text-purple-600 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-6 pb-6 text-foreground-700 leading-relaxed">
                  Key warning signs include: Loss of control (using more than intended, unable to cut down), Preoccupation (spending lots of time getting/using substances, intense cravings), Neglecting responsibilities at work or home, Risky use despite consequences, and Physical effects (tolerance and withdrawal symptoms). If you're experiencing several of these signs, speak with a doctor or addiction specialist at our clinic.
                </div>
              </details>

              <details className="bg-background-50 border border-background-200 rounded-lg overflow-hidden group">
                <summary className="cursor-pointer p-6 font-semibold text-lg text-foreground-950 hover:bg-background-100 transition-colors flex justify-between items-center">
                  What is the difference between dependence and addiction?
                  <i className="ri-arrow-down-s-line text-2xl text-purple-600 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-6 pb-6 text-foreground-700 leading-relaxed">
                  Physical dependence is when your body adapts to a substance and you experience tolerance and withdrawal. Addiction (or a severe Substance Use Disorder) is a brain disease that involves compulsive drug-seeking and use, regardless of the negative consequences. It is possible to be dependent on a medication without being addicted.
                </div>
              </details>

              <details className="bg-background-50 border border-background-200 rounded-lg overflow-hidden group">
                <summary className="cursor-pointer p-6 font-semibold text-lg text-foreground-950 hover:bg-background-100 transition-colors flex justify-between items-center">
                  Is addiction a choice?
                  <i className="ri-arrow-down-s-line text-2xl text-purple-600 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-6 pb-6 text-foreground-700 leading-relaxed">
                  The initial decision to use a substance is often a choice. However, once addiction develops, it changes the brain's structure and function in ways that severely impair impulse control, making it extremely difficult to stop without help. Addiction is considered a disease, not a moral failing.
                </div>
              </details>

              <details className="bg-background-50 border border-background-200 rounded-lg overflow-hidden group">
                <summary className="cursor-pointer p-6 font-semibold text-lg text-foreground-950 hover:bg-background-100 transition-colors flex justify-between items-center">
                  What treatment options are available in Thornhill?
                  <i className="ri-arrow-down-s-line text-2xl text-purple-600 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-6 pb-6 text-foreground-700 leading-relaxed">
                  Our <a href="/addiction-services" className="text-primary-700 hover:text-primary-800 underline underline-offset-2 transition-colors font-semibold">addiction pharmacy and clinic</a> in Thornhill offers comprehensive treatment including <a href="/methadone-treatment" className="text-primary-700 hover:text-primary-800 underline underline-offset-2 transition-colors font-semibold">Methadone maintenance</a>, <a href="/suboxone-treatment" className="text-primary-700 hover:text-primary-800 underline underline-offset-2 transition-colors font-semibold">Suboxone therapy</a>, Kadian programs, alcohol cessation support, and smoking cessation programs. We serve patients from Thornhill, Richmond Hill, Vaughan, and North York with personalized, evidence-based care.
                </div>
              </details>

              <details className="bg-background-50 border border-background-200 rounded-lg overflow-hidden group">
                <summary className="cursor-pointer p-6 font-semibold text-lg text-foreground-950 hover:bg-background-100 transition-colors flex justify-between items-center">
                  Why is relapse so common in addiction recovery?
                  <i className="ri-arrow-down-s-line text-2xl text-purple-600 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-6 pb-6 text-foreground-700 leading-relaxed">
                  Addiction is a chronic, relapsing disease. Relapse is common because addiction deeply affects brain circuits related to memory, reward, and stress. Triggers in a person's environment can create intense cravings that are difficult to manage. A relapse does not mean treatment has failed, but that the treatment plan needs to be adjusted.
                </div>
              </details>
            </div>

            {/* FAQ Schema.org JSON-LD */}
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  "mainEntity": [
                    {
                      "@type": "Question",
                      "name": "What is Addiction?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Addiction is a chronic, treatable brain disease characterized by compulsive drug seeking and use, despite harmful consequences. It fundamentally changes the brain\'s structure and function, particularly in areas related to reward, stress, and self-control. Clinically, a severe addiction is diagnosed as a Substance Use Disorder (SUD). It is not a lack of willpower."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "What are the signs of addiction?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Key warning signs include: Loss of control (using more than intended, unable to cut down), Preoccupation (spending lots of time getting/using substances, intense cravings), Neglecting responsibilities at work or home, Risky use despite consequences, and Physical effects (tolerance and withdrawal symptoms). If you\'re experiencing several of these signs, speak with a doctor or addiction specialist at our clinic."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "What is the difference between dependence and addiction?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Physical dependence is when your body adapts to a substance and you experience tolerance and withdrawal. Addiction (or a severe Substance Use Disorder) is a brain disease that involves compulsive drug-seeking and use, regardless of the negative consequences. It is possible to be dependent on a medication without being addicted."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Is addiction a choice?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "The initial decision to use a substance is often a choice. However, once addiction develops, it changes the brain\'s structure and function in ways that severely impair impulse control, making it extremely difficult to stop without help. Addiction is considered a disease, not a moral failing."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "What treatment options are available in Thornhill?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Our addiction pharmacy and clinic in Thornhill offers comprehensive treatment including Methadone maintenance, Suboxone therapy, Kadian programs, alcohol cessation support, and smoking cessation programs. We serve patients from Thornhill, Richmond Hill, Vaughan, and North York with personalized, evidence-based care."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Why is relapse so common in addiction recovery?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Addiction is a chronic, relapsing disease. Relapse is common because addiction deeply affects brain circuits related to memory, reward, and stress. Triggers in a person\'s environment can create intense cravings that are difficult to manage. A relapse does not mean treatment has failed, but that the treatment plan needs to be adjusted."
                      }
                    }
                  ]
                })
              }}
            />
          </section>

        </div>
      </article>

      <MobileStickyCTA />
      <Footer />
    </div>
  );
}
